import React, { useState, useMemo, useRef, useEffect } from 'react';
import {
  ResponsiveContainer, ComposedChart, Scatter, Line, XAxis, YAxis,
  Tooltip as ChartTooltip, CartesianGrid, Legend,
} from 'recharts';
import { samplePoints } from '../lib/utils';
import { toMetricPoints, formatLag } from '../lib/variogram-units';
import { analysisApi } from '../lib/api';
import { trackJob, fetchJobResult, gridResultToGeoJSON } from '../lib/jobs-api';
import { useTheme } from '../contexts/ThemeContext';

const MODELS = [
  { id: 'spherical', label: 'Spherical' },
  { id: 'exponential', label: 'Exponential' },
  { id: 'gaussian', label: 'Gaussian' },
  { id: 'linear', label: 'Linear' },
];

/** Variogram model γ(h) */
function modelValue(model, h, nugget, sill, range) {
  if (h <= 0) return 0;
  const c = sill - nugget;
  switch (model) {
    case 'spherical':
      return h < range ? nugget + c * (1.5 * h / range - 0.5 * (h / range) ** 3) : sill;
    case 'exponential':
      return nugget + c * (1 - Math.exp(-3 * h / range));
    case 'gaussian':
      return nugget + c * (1 - Math.exp(-3 * (h / range) ** 2));
    case 'linear':
      return h < range ? nugget + c * (h / range) : sill;
    default:
      return sill;
  }
}

function extractPoints(geojson, valueAttr) {
  const pts = (geojson?.features || [])
    .filter(f => f.geometry?.type === 'Point' && !f.properties?._excluded)
    .map((f, i) => ({
      lon: f.geometry.coordinates[0],
      lat: f.geometry.coordinates[1],
      value: parseFloat(f.properties?.[valueAttr]),
      label: `P${i}`,
    }))
    .filter(p => !isNaN(p.value) && isFinite(p.value));
  return samplePoints(pts, 5000);
}

/** Width/height of an element, 0 until it is laid out. recharts warns when it draws into 0x0 (V.30). */
function useElementSize() {
  const ref = useRef(null);
  const [size, setSize] = useState({ width: 0, height: 0 });
  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    const read = () => setSize((prev) => {
      const next = { width: Math.floor(el.clientWidth), height: Math.floor(el.clientHeight) };
      return prev.width === next.width && prev.height === next.height ? prev : next;
    });
    read();
    if (typeof ResizeObserver === 'undefined') return undefined;
    const ro = new ResizeObserver(read);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  return [ref, size];
}

/**
 * VariogramEditor — interactive variogram modeling (Surfer-style)
 *
 * Experimental variogram from the backend, model curve overlaid, and
 * nugget/sill/range sliders. Runs kriging with the tuned parameters.
 *
 * Props:
 *   layers   - [{ id, name, geojson }] point layers
 *   onResult(geojson, name, kind) - add kriging output to the map
 *   onClose()
 */
export default function VariogramEditor({ layers = [], onResult, onClose }) {
  const { C: themeC } = useTheme();
  const C = { ...themeC, amber: themeC.warnText };
  const [layerId, setLayerId] = useState(layers[0]?.id || '');
  const [valueAttr, setValueAttr] = useState('');
  const [model, setModel] = useState('spherical');
  const [experimental, setExperimental] = useState(null); // { lags, semivariance, sill, range, nugget }
  const [nugget, setNugget] = useState(0);
  const [sill, setSill] = useState(1);
  const [range, setRange] = useState(100); // metres
  const [mPerDeg, setMPerDeg] = useState(111320); // metres per degree, to hand the range to kriging
  const [chartRef, chartSize] = useElementSize();
  const [loading, setLoading] = useState(false);
  const [kriging, setKriging] = useState(false);
  const [error, setError] = useState(null);

  const layer = layers.find(l => l.id === layerId);

  const numericAttrs = useMemo(() => {
    const counts = {};
    (layer?.geojson?.features || []).slice(0, 300).forEach(f => {
      if (f.geometry?.type !== 'Point') return;
      Object.entries(f.properties || {}).forEach(([k, v]) => {
        if (k.startsWith('_')) return;
        const num = parseFloat(v);
        if (!isNaN(num) && isFinite(num)) counts[k] = (counts[k] || 0) + 1;
      });
    });
    return Object.keys(counts);
  }, [layer]);

  const loadVariogram = async () => {
    setError(null);
    setLoading(true);
    try {
      if (!valueAttr) throw new Error('Select a value attribute');
      const raw = extractPoints(layer?.geojson, valueAttr);
      // Distances in metres from the layer centroid, so the lag axis reads in metres (V.30)
      const metric = toMetricPoints(raw);
      const points = metric.points;
      setMPerDeg(metric.mPerDeg);
      if (points.length < 5) throw new Error(`Need ≥5 points with numeric "${valueAttr}"`);
      const res = await analysisApi.variogram({ points, n_lags: 15 });
      const d = res.data;
      if (!d.lags?.length) throw new Error('Not enough point pairs for a variogram');
      setExperimental(d);
      // Initialize sliders from the auto-fit
      setSill(d.sill || Math.max(...d.semivariance));
      setRange(d.range || d.lags[d.lags.length - 1] * 0.6);
      setNugget(0);
    } catch (err) {
      setError(err.response?.data?.detail || err.message);
    } finally {
      setLoading(false);
    }
  };

  // Chart data: experimental points + model curve, plus fit statistics
  const { chartData, fit } = useMemo(() => {
    if (!experimental) return { chartData: [], fit: null };
    const maxLag = experimental.lags[experimental.lags.length - 1];
    const curvePoints = 60;
    const rows = [];

    // Model curve samples
    for (let i = 0; i <= curvePoints; i++) {
      const h = (i / curvePoints) * maxLag * 1.05;
      rows.push({ h: Math.round(h * 1e6) / 1e6, model: modelValue(model, h, nugget, sill, range) });
    }
    // Experimental scatter merged in
    experimental.lags.forEach((lag, i) => {
      rows.push({ h: lag, exp: experimental.semivariance[i] });
    });
    rows.sort((a, b) => a.h - b.h);

    // Fit stats at experimental lags
    const obs = experimental.semivariance;
    const pred = experimental.lags.map(h => modelValue(model, h, nugget, sill, range));
    const meanObs = obs.reduce((s, v) => s + v, 0) / obs.length;
    const ssRes = obs.reduce((s, v, i) => s + (v - pred[i]) ** 2, 0);
    const ssTot = obs.reduce((s, v) => s + (v - meanObs) ** 2, 0) || 1e-12;
    return {
      chartData: rows,
      fit: {
        r2: Math.max(0, 1 - ssRes / ssTot),
        rmse: Math.sqrt(ssRes / obs.length),
      },
    };
  }, [experimental, model, nugget, sill, range]);

  const runKriging = async () => {
    setError(null);
    setKriging(true);
    try {
      const points = extractPoints(layer?.geojson, valueAttr);
      const res = await analysisApi.gridding({
        points,
        method: 'kriging',
        grid_resolution: 60,
        variogram_model: model === 'linear' ? 'spherical' : model,
        nugget, sill, range_param: range / mPerDeg, // the kriging endpoint works in degrees
      });
      const name = `Kriging (${model}, tuned): ${valueAttr} — ${layer.name}`;
      let geojson = res.data;
      if (res.status === 202 && res.data?.job) {
        // Large inputs are queued as a background job: wait for it and convert the compact grid.
        const job = await trackJob(res.data.job, { label: name });
        if (job.status !== 'succeeded') throw new Error(job.status === 'cancelled' ? 'Job cancelled' : (job.error || 'Job failed'));
        geojson = gridResultToGeoJSON(await fetchJobResult(job.id));
      }
      onResult(geojson, name, 'grid');
    } catch (err) {
      setError(err.response?.data?.detail || err.message);
    } finally {
      setKriging(false);
    }
  };

  const maxSV = experimental ? Math.max(...experimental.semivariance) : 1;
  const maxLag = experimental ? experimental.lags[experimental.lags.length - 1] : 1;

  const selStyle = { width: '100%', fontSize: 11, padding: '5px 8px', borderRadius: 6, background: C.bg4, border: `1px solid ${C.border}`, color: C.text0, outline: 'none' };
  const lbl = { fontSize: 10, color: C.text2, display: 'block', marginBottom: 2 };

  const slider = (label, value, setValue, min, max) => (
    <div>
      <label style={lbl}>{label} — <span style={{ color: C.cyan, fontFamily: 'monospace' }}>{value.toPrecision(4)}</span></label>
      <input type="range" min={min} max={max} step={(max - min) / 200 || 0.001} value={value}
        onChange={e => setValue(parseFloat(e.target.value))}
        style={{ width: '100%', accentColor: C.cyan }} />
    </div>
  );

  return (
    <div style={{ height: 300, background: C.bg2, borderTop: `1px solid ${C.border}`, display: 'flex', flexShrink: 0 }}>
      {/* Controls */}
      <div style={{ width: 230, borderRight: `1px solid ${C.border}`, padding: 10, display: 'flex', flexDirection: 'column', gap: 8, overflow: 'auto' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span style={{ fontSize: 12, fontWeight: 600, color: C.text0 }}>📉 Variogram</span>
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: C.text3, cursor: 'pointer', fontSize: 14 }}>✕</button>
        </div>

        <div>
          <label style={lbl}>Layer</label>
          <select value={layerId} onChange={e => { setLayerId(e.target.value); setValueAttr(''); setExperimental(null); }} style={selStyle}>
            {layers.map(l => <option key={l.id} value={l.id}>{l.name}</option>)}
          </select>
        </div>
        <div>
          <label style={lbl}>Value Attribute</label>
          <select value={valueAttr} onChange={e => { setValueAttr(e.target.value); setExperimental(null); }} style={selStyle}>
            <option value="">Select...</option>
            {numericAttrs.map(a => <option key={a} value={a}>{a}</option>)}
          </select>
        </div>

        {!experimental ? (
          <button onClick={loadVariogram} disabled={loading || !valueAttr}
            style={{ padding: '7px', borderRadius: 8, border: 'none', background: 'linear-gradient(135deg,#00b8de,#3b82f6)', color: '#fff', fontWeight: 600, fontSize: 11, cursor: 'pointer', opacity: (loading || !valueAttr) ? 0.5 : 1 }}>
            {loading ? '⏳ Computing…' : 'Compute Variogram'}
          </button>
        ) : (
          <>
            <div>
              <label style={lbl}>Model</label>
              <select value={model} onChange={e => setModel(e.target.value)} style={selStyle}>
                {MODELS.map(m => <option key={m.id} value={m.id}>{m.label}</option>)}
              </select>
            </div>
            {slider('Nugget', nugget, setNugget, 0, maxSV * 1.2)}
            {slider('Sill', sill, setSill, 0, maxSV * 2)}
            {slider('Range (m)', range, setRange, maxLag * 0.02, maxLag * 1.2)}

            {fit && (
              <div style={{ fontSize: 10, color: C.text2, fontFamily: 'monospace', lineHeight: 1.6, background: C.bg3, padding: '6px 8px', borderRadius: 6 }}>
                R² = <span style={{ color: fit.r2 > 0.8 ? C.emerald : fit.r2 > 0.5 ? C.amber : C.red }}>{fit.r2.toFixed(3)}</span><br />
                RMSE = {fit.rmse.toPrecision(4)}
              </div>
            )}

            <button onClick={runKriging} disabled={kriging}
              style={{ padding: '7px', borderRadius: 8, border: 'none', background: 'linear-gradient(135deg,#00b8de,#3b82f6)', color: '#fff', fontWeight: 600, fontSize: 11, cursor: 'pointer', opacity: kriging ? 0.5 : 1 }}>
              {kriging ? '⏳ Kriging…' : '▶ Run Kriging with these params'}
            </button>
            <button onClick={loadVariogram} disabled={loading}
              style={{ padding: '5px', borderRadius: 6, border: `1px solid ${C.border}`, background: C.bg4, color: C.text2, fontSize: 10, cursor: 'pointer' }}>
              ↻ Recompute experimental
            </button>
          </>
        )}

        {error && <div style={{ fontSize: 10, color: C.red, padding: '4px 8px', background: 'rgba(248,113,113,0.1)', borderRadius: 6 }}>✗ {error}</div>}
      </div>

      {/* Chart */}
      <div ref={chartRef} style={{ flex: 1, padding: '10px 14px', minWidth: 0, minHeight: 0 }}>
        {!experimental ? (
          <div style={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: C.text3, fontSize: 12, textAlign: 'center' }}>
            Select a layer + value attribute, then compute the experimental variogram.<br />
            Lag distance (h) vs semivariance γ(h) — tune the model to match the points.
          </div>
        ) : (chartSize.width > 0 && chartSize.height > 0) && (
          <ResponsiveContainer width="100%" height="100%">
            <ComposedChart data={chartData} margin={{ top: 8, right: 12, bottom: 16, left: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
              <XAxis dataKey="h" type="number" domain={[0, 'dataMax']}
                tick={{ fontSize: 9, fill: C.text3 }} stroke={C.border}
                label={{ value: 'Lag (m)', position: 'insideBottom', offset: -8, fontSize: 10, fill: C.text3 }}
                tickFormatter={v => formatLag(v)} />
              <YAxis tick={{ fontSize: 9, fill: C.text3 }} stroke={C.border}
                tickFormatter={v => v.toPrecision(2)} />
              <ChartTooltip contentStyle={{ background: C.bg1, border: `1px solid ${C.border}`, borderRadius: 8, fontSize: 11 }}
                itemStyle={{ color: C.text1 }} labelStyle={{ color: C.text0 }}
                formatter={(v) => typeof v === 'number' ? v.toPrecision(4) : v}
                labelFormatter={(v) => `Lag = ${formatLag(v)}`} />
              <Legend wrapperStyle={{ fontSize: 10 }} />
              <Scatter name="Experimental γ(h)" dataKey="exp" fill={C.amber} />
              <Line name={`${MODELS.find(m => m.id === model)?.label} model`} dataKey="model"
                stroke={C.cyan} strokeWidth={2} dot={false} connectNulls />
            </ComposedChart>
          </ResponsiveContainer>
        )}
      </div>
    </div>
  );
}
